import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getVersion,
  getApplication,
  createVersion,
  copyVersion,
  deleteVersion,
  updateApplication,
  updateVersion,
  dismissApplicationWarning,
  deleteApplication,
  listSecrets,
  listTeamUsers,
  uploadApplicationIcon,
  downloadApplicationIcon,
  deleteApplicationIcon,
  suggestColorPaletteFromIcon,
  createPublishJob,
  createMetadataUploadJob,
  createAppPageSiteDeployJob,
  enablePageHosting,
  disablePageHosting,
  listPageHostingOwners,
  listPageHostingRepos,
  triggerJob,
  getJob,
  validateIOSMetadataUploadReadiness,
  validateAndroidMetadataUploadReadiness,
  PLATFORM_IOS,
  PLATFORM_ANDROID,
  SECRET_TYPE_GITHUB,
  type Application,
  type AppVersion,
  type AppWarning,
  type ColorPalette,
  type CreateVersionInput,
  type UpdateApplicationInput,
  type GitHubOwnerChoice,
  type GitHubRepoChoice,
  type Job,
  type Secret,
  type TeamUser,
} from '../api/client';
import { Plus, Trash2, Upload, Download, Image, Copy, AlertTriangle, Loader2, Sparkles, Check, ChevronDown, Save } from 'lucide-react';
import OnboardingTour from './onboarding/OnboardingTour';
import clsx from 'clsx';
import { useApplication } from '../context/ApplicationContext';
import { useError } from '../context/ErrorContext';
import { panelPath } from '../config/paths';
import { useIsPro } from '../hooks/useIsPro';
import AppPageLayout from './AppPageLayout';
import AppIconImage from './AppIconImage';
import ColorSwatchPicker from './ColorSwatchPicker';
import ModalLayout from './ModalLayout';
import ConfirmDialog from './ConfirmDialog';
import PlatformSelectCards from './PlatformSelectCards';
import PageToolbar from './PageToolbar';
import ProTag from './ProTag';
import {
  APP_STORE_LOCALIZATIONS,
  getLocaleFlag,
} from '../constants/appStoreLocalizations';
import {
  AGE_RATING_PROFILES,
  CONTENT_RIGHTS_OPTIONS,
  DEFAULT_AGE_RATING_PROFILE,
  DEFAULT_CONTENT_RIGHTS,
  IOS_APP_CATEGORIES,
} from '../constants/appStoreReview';

const emptyVersionInput = (): CreateVersionInput => ({
  version: '',
});

const DEFAULT_COLOR_PALETTE: ColorPalette = {
  primary: '#2563eb',
  secondary: '#1e40af',
  accent: '#f59e0b',
  background: '#f8fafc',
  text: '#0f172a',
};

const APPLE_STANDARD_EULA_URL =
  'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/';

function paletteFromApp(a: Application | null | undefined): ColorPalette {
  const p = a?.colorPalette;
  return {
    primary: p?.primary || DEFAULT_COLOR_PALETTE.primary,
    secondary: p?.secondary || DEFAULT_COLOR_PALETTE.secondary,
    accent: p?.accent || DEFAULT_COLOR_PALETTE.accent,
    background: p?.background || DEFAULT_COLOR_PALETTE.background,
    text: p?.text || DEFAULT_COLOR_PALETTE.text,
  };
}

function palettesEqual(a: ColorPalette, b: ColorPalette): boolean {
  return (
    a.primary === b.primary &&
    a.secondary === b.secondary &&
    a.accent === b.accent &&
    a.background === b.background &&
    a.text === b.text
  );
}

/** Dropdown sentinel: yeni repo oluştur (backend otomatik isim üretir). */
const NEW_GITHUB_REPO = '__new__';

/** Mevcut version string'inden bir üst sürüm önerir (örn. 1.0 → 1.1, 2.0.1 → 2.0.2). */
function suggestNextVersion(current: string): string {
  const trimmed = current.trim();
  if (!trimmed) return '';
  const parts = trimmed.split('.').map(p => {
    const n = parseInt(p, 10);
    return Number.isFinite(n) ? n : null;
  });
  if (parts.every(p => p !== null)) {
    const next = [...parts] as number[];
    next[next.length - 1] += 1;
    return next.join('.');
  }
  const match = trimmed.match(/^(.*?)(\d+)$/);
  if (match) {
    return `${match[1]}${parseInt(match[2], 10) + 1}`;
  }
  return `${trimmed}.1`;
}

const ApplicationDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    app,
    loading: appLoading,
    error: appError,
    versions,
    setApp,
    refetchVersions,
  } = useApplication();
  const [appStoreSecrets, setAppStoreSecrets] = useState<Secret[]>([]);
  const [appStoreIapSecrets, setAppStoreIapSecrets] = useState<Secret[]>([]);
  const [playStoreSecrets, setPlayStoreSecrets] = useState<Secret[]>([]);
  const [githubSecrets, setGithubSecrets] = useState<Secret[]>([]);
  const [githubOwners, setGithubOwners] = useState<GitHubOwnerChoice[]>([]);
  const [githubRepos, setGithubRepos] = useState<GitHubRepoChoice[]>([]);
  const [githubOwner, setGithubOwner] = useState('');
  const [githubRepo, setGithubRepo] = useState(NEW_GITHUB_REPO);
  const [repoSearch, setRepoSearch] = useState('');
  const [repoDropdownOpen, setRepoDropdownOpen] = useState(false);
  const [repoMenuPos, setRepoMenuPos] = useState<{
    top: number;
    bottom: number;
    left: number;
    width: number;
    openUp: boolean;
  } | null>(null);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [reposLoading, setReposLoading] = useState(false);
  const repoSelectRef = React.useRef<HTMLDivElement>(null);
  const repoMenuRef = React.useRef<HTMLDivElement>(null);
  const [enablingPages, setEnablingPages] = useState(false);
  const [disablingPages, setDisablingPages] = useState(false);
  const [editingPageHosting, setEditingPageHosting] = useState(false);
  const [showDisablePagesModal, setShowDisablePagesModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySourceVersion, setCopySourceVersion] = useState<AppVersion | null>(null);
  const [copyForm, setCopyForm] = useState({ version: '', releaseNotes: '' });
  const [form, setForm] = useState<CreateVersionInput>(emptyVersionInput());
  const [submitting, setSubmitting] = useState(false);
  const [summaryEdit, setSummaryEdit] = useState('');
  const [paletteEdit, setPaletteEdit] = useState<ColorPalette>(DEFAULT_COLOR_PALETTE);
  const [paletteSuggesting, setPaletteSuggesting] = useState(false);
  const [paletteSuggestion, setPaletteSuggestion] = useState<ColorPalette | null>(null);
  const [paletteCopied, setPaletteCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteVersionTarget, setDeleteVersionTarget] = useState<AppVersion | null>(null);
  const [deletingVersion, setDeletingVersion] = useState(false);
  const [mobilForm, setMobilForm] = useState({
    iosBundleId: '',
    androidBundleId: '',
    appStoreSecretId: '',
    appStoreIapSecretId: '',
    playStoreSecretId: '',
    githubSecretId: '',
    primaryLanguage: 'en',
    copyright: '',
    primaryCategory: '',
    secondaryCategory: '',
    contentRightsDeclaration: DEFAULT_CONTENT_RIGHTS,
    ageRatingProfile: DEFAULT_AGE_RATING_PROFILE,
    reviewContactUserId: '',
    reviewNotes: '',
    eulaType: 'apple' as 'apple' | 'custom',
    eulaUrl: APPLE_STANDARD_EULA_URL,
    eulaText: '',
  });
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([]);
  const [iconUploading, setIconUploading] = useState(false);
  const [iconDeleting, setIconDeleting] = useState(false);
  const [iconDownloading, setIconDownloading] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [deployVersionId, setDeployVersionId] = useState('');
  const [deployVersion, setDeployVersion] = useState<AppVersion | null>(null);
  const [deployPlatforms, setDeployPlatforms] = useState<string[]>([PLATFORM_IOS, PLATFORM_ANDROID]);
  const [deployIncludeScreens, setDeployIncludeScreens] = useState(true);
  /** Default true: App Preview yalnızca primary locale. */
  const [deployAppPreviewPrimaryOnly, setDeployAppPreviewPrimaryOnly] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [fixWarning, setFixWarning] = useState<AppWarning | null>(null);
  const [fixNameValue, setFixNameValue] = useState('');
  const [fixWarningSaving, setFixWarningSaving] = useState(false);
  const [eulaGenerating, setEulaGenerating] = useState(false);
  const [eulaDeployJob, setEulaDeployJob] = useState<Job | null>(null);
  const iconInputRef = React.useRef<HTMLInputElement>(null);
  const { showError: showErrorPopup } = useError();
  const { isPro, goToUpgrade } = useIsPro();

  const mobilFormFromApp = useCallback((a: Application) => {
    const eulaType = a.eulaType === 'custom' ? 'custom' as const : 'apple' as const;
    return {
      iosBundleId: a.iosBundleId ?? '',
      androidBundleId: a.androidBundleId ?? '',
      appStoreSecretId: a.appStoreSecretId ?? '',
      appStoreIapSecretId: a.appStoreIapSecretId ?? '',
      playStoreSecretId: a.playStoreSecretId ?? '',
      githubSecretId: a.githubSecretId ?? '',
      primaryLanguage: a.primaryLanguage ?? 'en',
      copyright: a.copyright ?? '',
      primaryCategory: a.primaryCategory ?? '',
      secondaryCategory: a.secondaryCategory ?? '',
      contentRightsDeclaration: a.contentRightsDeclaration ?? DEFAULT_CONTENT_RIGHTS,
      ageRatingProfile: a.ageRatingProfile ?? DEFAULT_AGE_RATING_PROFILE,
      reviewContactUserId: a.reviewContactUserId ?? '',
      reviewNotes: a.reviewNotes ?? '',
      eulaType,
      eulaUrl: eulaType === 'apple' ? APPLE_STANDARD_EULA_URL : (a.eulaUrl ?? ''),
      eulaText: eulaType === 'custom' ? (a.eulaText ?? '') : '',
    };
  }, []);

  /** Secrets / team users — app + versions ApplicationContext’ten gelir. */
  useEffect(() => {
    if (!id || !app || app.id !== id || app.type !== 'mobil_app') {
      return;
    }
    let cancelled = false;
    Promise.all([
      listSecrets('app_store'),
      listSecrets('app_store_iap'),
      listSecrets('play_store'),
      listSecrets(SECRET_TYPE_GITHUB),
      listTeamUsers().catch(() => [] as TeamUser[]),
    ])
      .then(([appStore, appStoreIap, playStore, github, users]) => {
        if (cancelled) return;
        setAppStoreSecrets(appStore);
        setAppStoreIapSecrets(appStoreIap);
        setPlayStoreSecrets(playStore);
        setGithubSecrets(github);
        setTeamUsers(users);
      })
      .catch(e => {
        if (!cancelled) showErrorPopup(e instanceof Error ? e.message : t('common.loadFailed'));
      });
    return () => {
      cancelled = true;
    };
  }, [id, app, app?.id, app?.type, showErrorPopup]);

  useEffect(() => {
    const needOwners =
      Boolean(id && mobilForm.githubSecretId) &&
      (!app?.pageHosting?.enabled || editingPageHosting);
    if (!needOwners) {
      return;
    }
    let cancelled = false;
    setOwnersLoading(true);
    listPageHostingOwners(id!, mobilForm.githubSecretId)
      .then(owners => {
        if (cancelled) return;
        setGithubOwners(owners);
        setGithubOwner(prev => {
          if (app?.pageHosting?.owner && owners.some(o => o.login === app.pageHosting!.owner)) {
            return app.pageHosting.owner;
          }
          if (prev && owners.some(o => o.login === prev)) return prev;
          return owners[0]?.login ?? '';
        });
      })
      .catch(e => {
        if (!cancelled) {
          setGithubOwners([]);
          showErrorPopup(e instanceof Error ? e.message : t('panel.pageHostingOwnersFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) setOwnersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    id,
    mobilForm.githubSecretId,
    app?.pageHosting,
    app?.pageHosting?.enabled,
    app?.pageHosting?.owner,
    editingPageHosting,
    showErrorPopup,
    t,
  ]);

  useEffect(() => {
    const needRepos =
      Boolean(id && mobilForm.githubSecretId && githubOwner) &&
      (!app?.pageHosting?.enabled || editingPageHosting);
    if (!needRepos) {
      setGithubRepos([]);
      setGithubRepo(NEW_GITHUB_REPO);
      setRepoSearch('');
      setRepoDropdownOpen(false);
      return;
    }
    let cancelled = false;
    setReposLoading(true);
    listPageHostingRepos(id!, {
      secretId: mobilForm.githubSecretId,
      owner: githubOwner,
    })
      .then(repos => {
        if (cancelled) return;
        setGithubRepos(repos);
        const currentRepo =
          app?.pageHosting?.owner === githubOwner ? app.pageHosting.repo : '';
        setGithubRepo(prevRepo => {
          if (currentRepo && repos.some(r => r.name === currentRepo)) return currentRepo;
          if (prevRepo === NEW_GITHUB_REPO) return NEW_GITHUB_REPO;
          if (prevRepo && repos.some(r => r.name === prevRepo)) return prevRepo;
          return NEW_GITHUB_REPO;
        });
      })
      .catch(e => {
        if (!cancelled) {
          setGithubRepos([]);
          showErrorPopup(e instanceof Error ? e.message : t('panel.pageHostingReposFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) setReposLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    id,
    mobilForm.githubSecretId,
    githubOwner,
    app,
    editingPageHosting,
    showErrorPopup,
    t,
  ]);

  const applyAppToForm = useCallback(
    (a: Application) => {
      setSummaryEdit(a.summary ?? '');
      setPaletteEdit(paletteFromApp(a));
      if (a.type === 'mobil_app') {
        setMobilForm(mobilFormFromApp(a));
      }
      if (a.pageHosting?.owner) {
        setGithubOwner(a.pageHosting.owner);
      }
      if (a.pageHosting?.repo) {
        setGithubRepo(a.pageHosting.repo);
      }
    },
    [mobilFormFromApp]
  );

  const [hydratedAppId, setHydratedAppId] = useState<string | null>(null);
  if (app && app.id !== hydratedAppId) {
    setHydratedAppId(app.id);
    applyAppToForm(app);
  }

  const updateRepoMenuPos = useCallback(() => {
    const el = repoSelectRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuHeight = 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuHeight && rect.top > spaceBelow;
    setRepoMenuPos({
      top: rect.bottom + 4,
      bottom: window.innerHeight - rect.top + 4,
      left: rect.left,
      width: rect.width,
      openUp,
    });
  }, []);

  useEffect(() => {
    if (!repoDropdownOpen) return;
    updateRepoMenuPos();
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (repoSelectRef.current?.contains(target) || repoMenuRef.current?.contains(target)) {
        return;
      }
      setRepoDropdownOpen(false);
      setRepoSearch('');
    };
    const onReposition = () => updateRepoMenuPos();
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [repoDropdownOpen, updateRepoMenuPos]);

  const filteredGithubRepos = useMemo(() => {
    const q = repoSearch.trim().toLowerCase();
    if (!q) return githubRepos;
    return githubRepos.filter(r => {
      const name = r.name.toLowerCase();
      const full = (r.fullName ?? '').toLowerCase();
      return name.includes(q) || full.includes(q);
    });
  }, [githubRepos, repoSearch]);

  const selectedGithubRepoLabel = useMemo(() => {
    if (githubRepo === NEW_GITHUB_REPO) return t('panel.githubPagesCreateNewRepo');
    const found = githubRepos.find(r => r.name === githubRepo);
    if (!found) return githubRepo || t('panel.githubPagesRepoEmpty');
    return found.private
      ? `${found.name} (${t('panel.githubRepoPrivate')})`
      : found.name;
  }, [githubRepo, githubRepos, t]);

  const mobilFormDirty = useMemo(() => {
    if (!app || app.type !== 'mobil_app') return false;
    const baseline = mobilFormFromApp(app);
    return (Object.keys(baseline) as (keyof typeof baseline)[]).some(
      key => mobilForm[key] !== baseline[key]
    );
  }, [app, mobilForm, mobilFormFromApp]);

  const summaryDirty = summaryEdit !== (app?.summary ?? '');
  const paletteDirty = !palettesEqual(paletteEdit, paletteFromApp(app));
  const hasUnsavedChanges = summaryDirty || paletteDirty || mobilFormDirty;

  const buildUpdatePayload = useCallback((): UpdateApplicationInput => {
    const payload: UpdateApplicationInput = {
      summary: summaryEdit,
      colorPalette: paletteEdit,
    };
    if (app?.type === 'mobil_app') {
      payload.iosBundleId = mobilForm.iosBundleId;
      payload.androidBundleId = mobilForm.androidBundleId;
      payload.appStoreSecretId = mobilForm.appStoreSecretId;
      payload.appStoreIapSecretId = mobilForm.appStoreIapSecretId;
      payload.playStoreSecretId = mobilForm.playStoreSecretId;
      payload.githubSecretId = mobilForm.githubSecretId;
      payload.primaryLanguage = mobilForm.primaryLanguage;
      payload.copyright = mobilForm.copyright;
      payload.primaryCategory = mobilForm.primaryCategory;
      payload.secondaryCategory = mobilForm.secondaryCategory;
      payload.contentRightsDeclaration = mobilForm.contentRightsDeclaration;
      payload.ageRatingProfile = mobilForm.ageRatingProfile;
      payload.reviewContactUserId = mobilForm.reviewContactUserId;
      payload.reviewNotes = mobilForm.reviewNotes;
      payload.eulaType = mobilForm.eulaType;
      payload.eulaUrl = mobilForm.eulaType === 'apple' ? APPLE_STANDARD_EULA_URL : mobilForm.eulaUrl;
      payload.eulaText = mobilForm.eulaType === 'custom' ? mobilForm.eulaText : '';
    }
    return payload;
  }, [app?.type, mobilForm, paletteEdit, summaryEdit]);

  const persistApplication = useCallback(async (): Promise<Application> => {
    if (!id) throw new Error('missing id');
    const updated = await updateApplication(id, buildUpdatePayload());
    setApp(updated);
    applyAppToForm(updated);
    return updated;
  }, [id, buildUpdatePayload, setApp, applyAppToForm]);

  const handleSave = async () => {
    if (!id || !hasUnsavedChanges) return;
    setSaving(true);
    try {
      await persistApplication();
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('common.saveFailed');
      showErrorPopup(msg);
    } finally {
      setSaving(false);
    }
  };

  const pagesEnabled = Boolean(app?.pageHosting?.enabled);
  const eulaDeployInProgress =
    eulaGenerating ||
    eulaDeployJob?.status === 'pending' ||
    eulaDeployJob?.status === 'processing';

  const handleGenerateEula = async () => {
    if (!id || eulaDeployInProgress) return;
    if (!isPro) {
      goToUpgrade();
      return;
    }
    if (!pagesEnabled) {
      showErrorPopup(t('panel.pageHostingNotEnabled'));
      return;
    }
    setEulaGenerating(true);
    try {
      if (hasUnsavedChanges) {
        await persistApplication();
      }
      const { jobId } = await createAppPageSiteDeployJob(id, { pageTypes: ['eula'] });
      await triggerJob(jobId);
      const job = await getJob(jobId);
      setEulaDeployJob(job);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('panel.eulaGenerateFailed');
      showErrorPopup(msg);
    } finally {
      setEulaGenerating(false);
    }
  };

  useEffect(() => {
    const jobId = eulaDeployJob?.id;
    const status = eulaDeployJob?.status;
    if (!jobId || (status !== 'pending' && status !== 'processing')) return;
    const timer = setInterval(() => {
      getJob(jobId)
        .then(updated => setEulaDeployJob(updated))
        .catch(() => {});
    }, 2000);
    return () => clearInterval(timer);
  }, [eulaDeployJob?.id, eulaDeployJob?.status]);

  useEffect(() => {
    if (eulaDeployJob?.status !== 'completed' || !id) return;
    let cancelled = false;
    void getApplication(id)
      .then(updated => {
        if (cancelled) return;
        setApp(updated);
        const next = mobilFormFromApp(updated);
        setMobilForm(f => ({
          ...f,
          eulaType: next.eulaType,
          eulaUrl: next.eulaUrl,
          eulaText: next.eulaText,
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [eulaDeployJob?.status, id, setApp, mobilFormFromApp]);

  const handlePaletteColorChange = (key: keyof ColorPalette, color: string) => {
    if (paletteSuggestion) return;
    setPaletteEdit(prev => ({ ...prev, [key]: color }));
  };

  const hasIcon = Boolean(app?.iconPath || app?.iconUpdatedAt);

  const handleSuggestPaletteFromIcon = async () => {
    if (!id) return;
    if (!isPro) {
      goToUpgrade();
      return;
    }
    if (!hasIcon) {
      showErrorPopup(t('panel.suggestPaletteNeedIcon'));
      return;
    }
    setPaletteSuggesting(true);
    try {
      const res = await suggestColorPaletteFromIcon(id);
      if (!res?.colorPalette) {
        throw new Error(t('panel.colorPalette'));
      }
      setPaletteSuggestion({
        primary: res.colorPalette.primary || DEFAULT_COLOR_PALETTE.primary,
        secondary: res.colorPalette.secondary || DEFAULT_COLOR_PALETTE.secondary,
        accent: res.colorPalette.accent || DEFAULT_COLOR_PALETTE.accent,
        background: res.colorPalette.background || DEFAULT_COLOR_PALETTE.background,
        text: res.colorPalette.text || DEFAULT_COLOR_PALETTE.text,
      });
    } catch (e) {
      showErrorPopup(e instanceof Error ? e.message : t('panel.colorPalette'));
    } finally {
      setPaletteSuggesting(false);
    }
  };

  const handleAcceptPaletteSuggestion = () => {
    if (!paletteSuggestion) return;
    setPaletteEdit(paletteSuggestion);
    setPaletteSuggestion(null);
  };

  const handleDiscardPaletteSuggestion = () => {
    setPaletteSuggestion(null);
  };

  const handleCopyPaletteHex = async () => {
    const palette = paletteSuggestion ?? paletteEdit;
    const text = [
      `primary=${palette.primary}`,
      `secondary=${palette.secondary}`,
      `accent=${palette.accent}`,
      `background=${palette.background}`,
      `text=${palette.text}`,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setPaletteCopied(true);
      window.setTimeout(() => setPaletteCopied(false), 2000);
    } catch {
      showErrorPopup(t('panel.copyPaletteHexFailed'));
    }
  };

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!id || !file || !file.type.startsWith('image/')) return;
    setIconUploading(true);
    try {
      const updated = await uploadApplicationIcon(id, file);
      setApp(updated);
    } catch (err) {
      showErrorPopup(err);
    } finally {
      setIconUploading(false);
    }
  };

  const handleIconDownload = async () => {
    if (!id || !(app?.iconPath || app?.iconUpdatedAt)) return;
    setIconDownloading(true);
    try {
      const safeName = app.name.trim().replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '') || 'app-icon';
      await downloadApplicationIcon(id, `${safeName}-icon.png`, app.iconPath);
    } catch (err) {
      showErrorPopup(err);
    } finally {
      setIconDownloading(false);
    }
  };

  const handleIconDelete = async () => {
    if (!id || !(app?.iconPath || app?.iconUpdatedAt)) return;
    setIconDeleting(true);
    try {
      const updated = await deleteApplicationIcon(id);
      setApp(updated);
      setPaletteSuggestion(null);
    } catch (err) {
      showErrorPopup(err);
    } finally {
      setIconDeleting(false);
    }
  };

  const openCreateModal = () => {
    setForm({ ...emptyVersionInput(), version: '' });
    setModalOpen(true);
  };

  const openCopyModal = (source: AppVersion, e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    setCopySourceVersion(source);
    setCopyForm({
      version: suggestNextVersion(source.version),
      releaseNotes: '',
    });
    setCopyModalOpen(true);
  };

  const openDeleteVersionConfirm = (v: AppVersion, e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (deletingVersion) return;
    setDeleteVersionTarget(v);
  };

  const handleDeleteVersionConfirm = async () => {
    if (!id || !deleteVersionTarget || deletingVersion) return;
    setDeletingVersion(true);
    try {
      await deleteVersion(id, deleteVersionTarget.id);
      setDeleteVersionTarget(null);
      await refetchVersions();
    } catch (e) {
      showErrorPopup(e instanceof Error ? e.message : t('panel.deleteVersionFailed'));
    } finally {
      setDeletingVersion(false);
    }
  };

  const handleCopyVersion = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!id || !copySourceVersion || !copyForm.version.trim()) return;
    setSubmitting(true);
    try {
      const created = await copyVersion(id, copySourceVersion.id, {
        version: copyForm.version.trim(),
        releaseNotes: copyForm.releaseNotes.trim(),
      });
      setCopyModalOpen(false);
      setCopySourceVersion(null);
      await refetchVersions();
      if (created.translationJobId) {
        triggerJob(created.translationJobId).catch(err => showErrorPopup(err));
      }
      navigate(panelPath(`/applications/${id}/versions/${created.id}/information`));
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('applicationDetail.versionCopyFailed');
      showErrorPopup(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateVersion = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!id || !form.version.trim()) return;
    setSubmitting(true);
    try {
      await createVersion(id, { version: form.version.trim() });
      setModalOpen(false);
      await refetchVersions();
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('common.saveFailed');
      showErrorPopup(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDeployModal = async (opts?: {
    versionId?: string;
    /** Version satırından metadata upload: screens kapalı varsayılan. */
    metadataOnly?: boolean;
    event?: React.MouseEvent;
  }) => {
    opts?.event?.stopPropagation();
    if (!isPro) {
      goToUpgrade();
      return;
    }
    if (versions.length === 0) {
      showErrorPopup(t('panel.exportFastlaneNoVersions'));
      return;
    }
    const initialId = opts?.versionId || deployVersionId || versions[0]?.id || '';
    setDeployVersionId(initialId);
    setDeployPlatforms(prev => (prev.length ? prev : [PLATFORM_IOS, PLATFORM_ANDROID]));
    setDeployIncludeScreens(!(opts?.metadataOnly ?? false));
    setDeployAppPreviewPrimaryOnly(true);
    setShowDeployModal(true);
    if (id && initialId) {
      try {
        const v = await getVersion(id, initialId);
        setDeployVersion(v);
      } catch (e) {
        showErrorPopup(e);
      }
    }
  };

  const handleDeployVersionChange = async (versionId: string) => {
    setDeployVersionId(versionId);
    setDeployVersion(null);
    if (!id || !versionId) return;
    try {
      const v = await getVersion(id, versionId);
      setDeployVersion(v);
    } catch (e) {
      showErrorPopup(e);
    }
  };

  const toggleDeployPlatform = (p: string) => {
    setDeployPlatforms(prev => (prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]));
  };

  const iosDeployIssues = useMemo(
    () => validateIOSMetadataUploadReadiness(app, deployVersion),
    [app, deployVersion]
  );
  const androidDeployIssues = useMemo(
    () => validateAndroidMetadataUploadReadiness(app, deployVersion),
    [app, deployVersion]
  );

  const handleConfirmDeploy = async () => {
    if (!id) return;
    if (!deployVersionId) {
      showErrorPopup(t('panel.exportFastlaneNeedVersion'));
      return;
    }
    if (deployPlatforms.length === 0) {
      showErrorPopup(t('panel.selectPlatformForUpload'));
      return;
    }
    if (deployPlatforms.includes(PLATFORM_IOS) && !(deployVersion?.supportUrl ?? '').trim()) {
      showErrorPopup(t('panel.metadataUploadSupportUrlRequired'));
      return;
    }
    setDeploying(true);
    try {
      const { jobId } = deployIncludeScreens
        ? await createPublishJob(id, deployVersionId, deployPlatforms, true, {
            localizeAppPreviews:
              deployPlatforms.includes(PLATFORM_IOS) && !deployAppPreviewPrimaryOnly,
          })
        : await createMetadataUploadJob(id, deployVersionId, deployPlatforms);
      await triggerJob(jobId);
      setShowDeployModal(false);
      navigate(panelPath(`/jobs/${jobId}`));
    } catch (e) {
      showErrorPopup(e);
    } finally {
      setDeploying(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const openFixWarning = async (w: AppWarning) => {
    if (!id) return;
    setFixWarning(w);
    setFixNameValue('');
    if (w.code === 'ASC_NAME_IN_USE' && w.versionId && w.locale) {
      try {
        const v = await getVersion(id, w.versionId);
        const platform = w.platform || PLATFORM_IOS;
        const name =
          v.localizations?.[platform]?.nameLocalized?.[w.locale] ??
          v.nameLocalized?.[w.locale] ??
          '';
        setFixNameValue(name);
      } catch (e) {
        showErrorPopup(e);
      }
    }
  };

  const handleSaveFixWarning = async () => {
    if (!id || !fixWarning) return;
    const w = fixWarning;
    const name = fixNameValue.trim();
    if (w.code === 'ASC_NAME_IN_USE') {
      if (!w.versionId || !w.locale) {
        showErrorPopup(t('panel.appWarningNoVersion'));
        return;
      }
      if (!name) return;
      setFixWarningSaving(true);
      try {
        const v = await getVersion(id, w.versionId);
        const platform = w.platform || PLATFORM_IOS;
        const otherPlatform = platform === PLATFORM_IOS ? PLATFORM_ANDROID : PLATFORM_IOS;
        const platLoc = {
          nameLocalized: { ...(v.localizations?.[platform]?.nameLocalized ?? v.nameLocalized ?? {}) },
          subtitleLocalized: {
            ...(v.localizations?.[platform]?.subtitleLocalized ?? v.subtitleLocalized ?? {}),
          },
          promotionalTextLocalized: {
            ...(v.localizations?.[platform]?.promotionalTextLocalized ??
              v.promotionalTextLocalized ??
              {}),
          },
          descriptionLocalized: {
            ...(v.localizations?.[platform]?.descriptionLocalized ?? v.descriptionLocalized ?? {}),
          },
          keywordsLocalized: {
            ...(v.localizations?.[platform]?.keywordsLocalized ?? v.keywordsLocalized ?? {}),
          },
          releaseNotesLocalized: {
            ...(v.localizations?.[platform]?.releaseNotesLocalized ?? {}),
          },
        };
        platLoc.nameLocalized[w.locale] = name;
        const otherLoc = v.localizations?.[otherPlatform];
        await updateVersion(id, w.versionId, {
          localizations: {
            [platform]: platLoc,
            ...(otherLoc
              ? {
                  [otherPlatform]: {
                    nameLocalized: { ...(otherLoc.nameLocalized ?? {}) },
                    subtitleLocalized: { ...(otherLoc.subtitleLocalized ?? {}) },
                    promotionalTextLocalized: { ...(otherLoc.promotionalTextLocalized ?? {}) },
                    descriptionLocalized: { ...(otherLoc.descriptionLocalized ?? {}) },
                    keywordsLocalized: { ...(otherLoc.keywordsLocalized ?? {}) },
                    releaseNotesLocalized: { ...(otherLoc.releaseNotesLocalized ?? {}) },
                  },
                }
              : {}),
          },
        });
        const updated = await dismissApplicationWarning(id, w.id);
        setApp(updated);
        setFixWarning(null);
        await refetchVersions();
      } catch (e) {
        showErrorPopup(e);
      } finally {
        setFixWarningSaving(false);
      }
      return;
    }
    // Unknown codes: dismiss only
    setFixWarningSaving(true);
    try {
      const updated = await dismissApplicationWarning(id, w.id);
      setApp(updated);
      setFixWarning(null);
    } catch (e) {
      showErrorPopup(e);
    } finally {
      setFixWarningSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!id || !app) return;
    setDeleting(true);
    try {
      await deleteApplication(id);
      setDeleteModalOpen(false);
      navigate(panelPath('/applications'));
    } catch (e) {
      showErrorPopup(e);
    } finally {
      setDeleting(false);
    }
  };

  if (!id) return null;

  const isMobilApp = app?.type === 'mobil_app';

  return (
    <AppPageLayout
      app={app}
      loading={appLoading}
      error={appError}
    >
      {app && (
      <>
      <PageToolbar className="justify-end" standalone>
          <div data-tour="appdetail-deploy" className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => {
                  void handleSave();
                }}
                disabled={!hasUnsavedChanges || saving}
                className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400 shadow-sm disabled:opacity-50 disabled:pointer-events-none transition-colors"
              >
                {saving ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    {t('common.saving')}
                  </span>
                ) : (
                  <>
                    <Save className="h-4 w-4 shrink-0" />
                    {t('common.save')}
                  </>
                )}
              </button>
              {isMobilApp && (
              <button
                type="button"
                onClick={() => {
                  void handleOpenDeployModal();
                }}
                disabled={deploying || versions.length === 0}
                title={
                  versions.length === 0
                    ? t('panel.exportFastlaneNoVersions')
                    : t('panel.exportFastlaneSubtitle')
                }
                className={
                  isPro
                    ? 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors'
                    : 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 cursor-pointer'
                }
              >
                {deploying ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-500 border-t-transparent" />
                    {t('panel.exportFastlaneRunning')}
                  </span>
                ) : (
                  <>
                    <Upload className="h-4 w-4 shrink-0" />
                    {t('panel.deploy')}
                    {!isPro && <ProTag />}
                  </>
                )}
              </button>
              )}
            </div>
          <div className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-600 shrink-0" aria-hidden />
          <button
            type="button"
            onClick={handleDeleteClick}
            disabled={deleting}
            className="inline-flex items-center justify-center gap-1.5 h-8 px-2.5 rounded-lg text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
            title={t('applicationDetail.deleteApp')}
          >
            <Trash2 className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline">{t('applicationDetail.deleteApp')}</span>
          </button>
        </PageToolbar>

      {app.warnings && app.warnings.length > 0 && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800/60 bg-amber-50/80 dark:bg-amber-950/30 overflow-hidden">
          <div className="p-4 border-b border-amber-200/80 dark:border-amber-800/50">
            <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-100 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {t('panel.appWarningsTitle')}
            </h2>
          </div>
          <ul className="divide-y divide-amber-200/70 dark:divide-amber-800/40">
            {app.warnings.map(w => (
              <li key={w.id}>
                <button
                  type="button"
                  onClick={() => {
                    void openFixWarning(w);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-amber-100/70 dark:hover:bg-amber-900/30 transition-colors flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-amber-950 dark:text-amber-50">
                      {w.message}
                    </p>
                    {(w.locale || w.field) && (
                      <p className="mt-0.5 text-xs font-mono text-amber-800/80 dark:text-amber-200/70">
                        {[w.locale, w.field, w.platform].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 text-xs font-medium text-amber-800 dark:text-amber-200 underline underline-offset-2">
                    {t('panel.appWarningsFix')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div data-tour="appdetail-icon-colors" className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('panel.iconAndColors')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {t('panel.iconAndColorsHint')}
              </p>
            </div>
            <button
              type="button"
              onClick={handleSuggestPaletteFromIcon}
              disabled={paletteSuggesting || !!paletteSuggestion || (isPro && !hasIcon)}
              title={!hasIcon ? t('panel.suggestPaletteNeedIcon') : undefined}
              className={
                isPro
                  ? 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400 shadow-sm disabled:opacity-50 transition-colors'
                  : 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 ring-1 ring-inset ring-amber-200/80 dark:ring-amber-800/60 hover:bg-amber-100 dark:hover:bg-amber-900/30 disabled:opacity-50 transition-colors'
              }
            >
              {paletteSuggesting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {t('panel.suggestPaletteFromIcon')}
              {!isPro && <ProTag />}
            </button>
          </div>
        </div>
        <div className="p-4 flex flex-col sm:flex-row sm:items-start gap-6">
          <input
            ref={iconInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            className="hidden"
            onChange={handleIconUpload}
          />
          <div className="relative group w-24 h-24 flex-shrink-0 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-600 shadow-sm">
            <button
              type="button"
              onClick={() => !iconUploading && iconInputRef.current?.click()}
              disabled={iconUploading}
              className="absolute inset-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 disabled:cursor-wait"
              title={hasIcon ? t('applicationDetail.changeIcon') : t('applicationDetail.uploadIcon')}
            >
              <AppIconImage
                appId={app.id}
                iconPath={app.iconPath}
                iconUpdatedAt={app.iconUpdatedAt}
                className="w-full h-full object-cover"
                alt={`${app.name} icon`}
                fallback={
                  <div className="w-full h-full border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center gap-1 bg-gray-50 dark:bg-gray-900/40">
                    <Image className="h-7 w-7 text-gray-400" />
                    <span className="text-[10px] font-medium text-gray-400">{t('applicationDetail.upload')}</span>
                  </div>
                }
              />
              <span
                className={`absolute inset-0 flex items-center justify-center bg-black/45 transition-opacity ${
                  iconUploading
                    ? 'opacity-100'
                    : 'opacity-0 group-hover:opacity-100'
                }`}
              >
                {iconUploading ? (
                  <Loader2 className="h-6 w-6 text-white animate-spin" />
                ) : (
                  <Upload className="h-6 w-6 text-white drop-shadow" />
                )}
              </span>
            </button>
            {hasIcon && (
              <>
                <button
                  type="button"
                  onClick={handleIconDownload}
                  disabled={iconDownloading || iconUploading}
                  className="absolute left-0.5 bottom-0.5 z-10 inline-flex items-center justify-center h-6 w-6 rounded-sm bg-transparent text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)] hover:bg-black/25 disabled:opacity-50"
                  title={t('applicationDetail.downloadIcon')}
                >
                  {iconDownloading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleIconDelete}
                  disabled={iconDeleting || iconUploading}
                  className="absolute right-0.5 bottom-0.5 z-10 inline-flex items-center justify-center h-6 w-6 rounded-sm bg-transparent text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)] hover:bg-black/25 disabled:opacity-50"
                  title={t('applicationDetail.removeIcon')}
                >
                  {iconDeleting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                </button>
              </>
            )}
          </div>

          <div className="flex-1 min-w-0 space-y-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCopyPaletteHex}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                title={t('panel.copyColorPalette')}
              >
                {paletteCopied ? (
                  <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
                {paletteCopied ? t('panel.paletteHexCopied') : t('panel.copyColorPalette')}
              </button>
              {paletteSuggesting && (
                <Loader2 className="h-4 w-4 text-gray-400 animate-spin" aria-hidden />
              )}
            </div>
            <div className="flex flex-wrap gap-6">
              {(
                [
                  ['primary', 'panel.colorPrimary'],
                  ['secondary', 'panel.colorSecondary'],
                  ['accent', 'panel.colorAccent'],
                  ['background', 'panel.colorBackground'],
                  ['text', 'panel.colorText'],
                ] as const
              ).map(([key, labelKey]) => (
                <ColorSwatchPicker
                  key={key}
                  label={t(labelKey)}
                  value={paletteSuggestion ? paletteSuggestion[key] : paletteEdit[key]}
                  onChange={color => handlePaletteColorChange(key, color)}
                  className={paletteSuggestion ? 'opacity-90 pointer-events-none' : undefined}
                />
              ))}
            </div>
            {paletteSuggestion && (
              <div className="rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/20 p-3 flex flex-wrap items-center gap-3">
                <span className="text-sm text-primary-800 dark:text-primary-200">
                  {t('panel.paletteSuggestion')}
                </span>
                <div className="flex items-center gap-1.5">
                  {(
                    ['primary', 'secondary', 'accent', 'background', 'text'] as const
                  ).map(key => (
                    <span
                      key={key}
                      className="h-6 w-6 rounded-md border border-gray-300 dark:border-gray-600"
                      style={{ backgroundColor: paletteSuggestion[key] }}
                      title={`${key}: ${paletteSuggestion[key]}`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    type="button"
                    onClick={handleAcceptPaletteSuggestion}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  >
                    <Check className="h-3.5 w-3.5" />
                    {t('panel.acceptPalette')}
                  </button>
                  <button
                    type="button"
                    onClick={handleDiscardPaletteSuggestion}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    {t('panel.discardPalette')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('applicationDetail.appSummary')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {t('applicationDetail.appSummaryHint')}
          </p>
        </div>
        <div className="p-4">
          <textarea
            value={summaryEdit}
            onChange={e => setSummaryEdit(e.target.value)}
            placeholder={t('applicationDetail.appSummaryPlaceholder')}
            rows={4}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400"
          />
        </div>
        </div>

      {isMobilApp && (
        <>
          <div data-tour="appdetail-versions" className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden mt-4">
            <div className="flex items-center gap-2 p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('applicationDetail.versionsTitle')}
              </h2>
              <button
                type="button"
                onClick={openCreateModal}
                title={t('applicationDetail.addVersion')}
                className="p-1.5 rounded-lg text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:text-gray-400 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/40">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      {t('applicationDetail.versionColumnLabel')}
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide w-32">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {versions.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-4 py-6 text-center text-gray-500 text-sm">
                        {t('applicationDetail.noVersionsYet')}
                      </td>
                    </tr>
                  ) : (
                    versions.map(v => (
                      <tr
                        key={v.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(panelPath(`/applications/${id}/versions/${v.id}/information`))}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(panelPath(`/applications/${id}/versions/${v.id}/information`));
                          }
                        }}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          {v.version}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-0.5">
                            <button
                              type="button"
                              onClick={e => {
                                void handleOpenDeployModal({
                                  versionId: v.id,
                                  metadataOnly: true,
                                  event: e,
                                });
                              }}
                              disabled={deploying}
                              title={t('panel.metadataUpload')}
                              aria-label={t('panel.metadataUpload')}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 disabled:opacity-50 transition-colors"
                            >
                              <Upload className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={e => openCopyModal(v, e)}
                              title={t('panel.copyVersion')}
                              aria-label={t('panel.copyVersion')}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={e => openDeleteVersionConfirm(v, e)}
                              title={t('panel.deleteVersion')}
                              aria-label={t('panel.deleteVersion')}
                              disabled={deletingVersion}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div data-tour="appdetail-general-info" className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden mt-4">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('applicationDetail.generalInfoTitle')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {t('applicationDetail.generalInfoHint')}
              </p>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('applicationDetail.iosBundleId')}
                  </label>
                  <input
                    type="text"
                    value={mobilForm.iosBundleId}
                    onChange={e =>
                      setMobilForm(f => ({ ...f, iosBundleId: e.target.value }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                    placeholder={t('applicationDetail.bundleIdPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('applicationDetail.androidBundleId')}
                  </label>
                  <input
                    type="text"
                    value={mobilForm.androidBundleId}
                    onChange={e =>
                      setMobilForm(f => ({
                        ...f,
                        androidBundleId: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                    placeholder={t('applicationDetail.bundleIdPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('applicationDetail.primaryLanguage')}
                  </label>
                  <select
                    value={mobilForm.primaryLanguage}
                    onChange={e => setMobilForm(f => ({ ...f, primaryLanguage: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    {APP_STORE_LOCALIZATIONS.map(l => (
                      <option key={l.value} value={l.value}>
                        {getLocaleFlag(l.value)} {l.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('applicationDetail.copyright')}
                  </label>
                  <input
                    type="text"
                    value={mobilForm.copyright}
                    onChange={e => setMobilForm(f => ({ ...f, copyright: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder={t('applicationDetail.copyrightPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('panel.primaryCategoryLabel')}
                  </label>
                  <select
                    value={mobilForm.primaryCategory}
                    onChange={e => {
                      const primaryCategory = e.target.value;
                      setMobilForm(f => ({
                        ...f,
                        primaryCategory,
                        secondaryCategory:
                          f.secondaryCategory === primaryCategory ? '' : f.secondaryCategory,
                      }));
                    }}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">—</option>
                    {IOS_APP_CATEGORIES.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('panel.secondaryCategoryLabel')}
                  </label>
                  <select
                    value={mobilForm.secondaryCategory}
                    onChange={e => setMobilForm(f => ({ ...f, secondaryCategory: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">{t('panel.secondaryCategoryNone')}</option>
                    {IOS_APP_CATEGORIES.filter(opt => opt.value !== mobilForm.primaryCategory).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('panel.secondaryCategoryHint')}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('panel.contentRightsLabel')}
                  </label>
                  <select
                    value={mobilForm.contentRightsDeclaration}
                    onChange={e => setMobilForm(f => ({ ...f, contentRightsDeclaration: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    {CONTENT_RIGHTS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('panel.ageRatingProfileLabel')}
                  </label>
                  <select
                    value={mobilForm.ageRatingProfile}
                    onChange={e => setMobilForm(f => ({ ...f, ageRatingProfile: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    {AGE_RATING_PROFILES.map(opt => (
                      <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t(AGE_RATING_PROFILES.find(p => p.value === mobilForm.ageRatingProfile)?.descKey ?? 'panel.ageRatingStandard4PlusDesc')}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('panel.reviewContactUserLabel')}
                  </label>
                  <select
                    value={mobilForm.reviewContactUserId}
                    onChange={e => setMobilForm(f => ({ ...f, reviewContactUserId: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">—</option>
                    {teamUsers.map(tu => (
                      <option key={tu.id} value={tu.id}>
                        {tu.label}{tu.isDefault ? ` (${t('panel.teamUserDefault')})` : ''} — {tu.firstName} {tu.lastName}
                      </option>
                    ))}
                  </select>
                  {!mobilForm.reviewContactUserId && (
                    <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                      {t('panel.reviewContactUserHint')}{' '}
                      <Link to={panelPath('/team-users')} className="underline">{t('nav.teamUsers')}</Link>
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('panel.versionReviewNotesLabel')}
                  </label>
                  <textarea
                    rows={3}
                    value={mobilForm.reviewNotes}
                    onChange={e => setMobilForm(f => ({ ...f, reviewNotes: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder={t('panel.versionReviewNotesPlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('panel.versionReviewNotesHint')}
                  </p>
                </div>
                <div className="md:col-span-2 space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('panel.eulaType')}
                    </label>
                    {mobilForm.eulaType === 'custom' && (
                      <button
                        type="button"
                        disabled={eulaDeployInProgress || !pagesEnabled}
                        onClick={() => void handleGenerateEula()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={!pagesEnabled ? t('panel.pageHostingNotEnabled') : undefined}
                      >
                        {eulaDeployInProgress ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="h-3.5 w-3.5" />
                        )}
                        {t('panel.generateEula')}
                        {!isPro ? <ProTag /> : null}
                      </button>
                    )}
                  </div>
                  {!pagesEnabled && mobilForm.eulaType === 'custom' && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('panel.pageHostingNotEnabledHint')}
                    </p>
                  )}
                  {eulaDeployJob && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {eulaDeployJob.status === 'completed'
                        ? t('panel.eulaGenerateCompleted')
                        : eulaDeployJob.status === 'failed'
                          ? t('panel.eulaGenerateFailed')
                          : t('panel.eulaGenerateInProgress')}
                      {' · '}
                      <Link to={panelPath(`/jobs/${eulaDeployJob.id}`)} className="underline">
                        {t('panel.jobDetailLink')}
                      </Link>
                    </p>
                  )}
                  <div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setMobilForm(f => ({
                            ...f,
                            eulaType: 'apple',
                            eulaUrl: APPLE_STANDARD_EULA_URL,
                            eulaText: '',
                          }))
                        }
                        className={clsx(
                          'px-3 py-1.5 rounded-lg text-sm border',
                          mobilForm.eulaType === 'apple'
                            ? 'border-primary-600 bg-primary-50 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                        )}
                      >
                        {t('panel.eulaTypeApple')}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setMobilForm(f => ({
                            ...f,
                            eulaType: 'custom',
                            eulaUrl:
                              f.eulaType === 'custom' && f.eulaUrl && f.eulaUrl !== APPLE_STANDARD_EULA_URL
                                ? f.eulaUrl
                                : '',
                            eulaText: f.eulaType === 'custom' ? f.eulaText : '',
                          }))
                        }
                        className={clsx(
                          'px-3 py-1.5 rounded-lg text-sm border',
                          mobilForm.eulaType === 'custom'
                            ? 'border-primary-600 bg-primary-50 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                        )}
                      >
                        {t('panel.eulaTypeCustom')}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {mobilForm.eulaType === 'custom'
                        ? t('panel.eulaTypeCustomHint')
                        : t('panel.eulaTypeAppleHint')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('panel.eulaUrl')}
                    </label>
                    <input
                      type="url"
                      value={mobilForm.eulaUrl}
                      onChange={e =>
                        setMobilForm(f => ({
                          ...f,
                          eulaUrl: e.target.value,
                          eulaType: 'custom',
                        }))
                      }
                      readOnly={mobilForm.eulaType === 'apple'}
                      className={clsx(
                        'w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 text-sm',
                        mobilForm.eulaType === 'apple'
                          ? 'border-gray-200 dark:border-gray-700 text-gray-500'
                          : 'border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white'
                      )}
                      placeholder={
                        mobilForm.eulaType === 'custom'
                          ? t('panel.eulaUrlCustomPlaceholder')
                          : APPLE_STANDARD_EULA_URL
                      }
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {t('panel.eulaUrlHint')}
                    </p>
                  </div>
                  {mobilForm.eulaType === 'custom' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('panel.eulaText')}
                      </label>
                      <textarea
                        value={mobilForm.eulaText}
                        onChange={e =>
                          setMobilForm(f => ({
                            ...f,
                            eulaText: e.target.value,
                            eulaType: 'custom',
                          }))
                        }
                        rows={10}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 font-mono text-xs leading-relaxed text-gray-900 dark:text-white"
                        placeholder={t('panel.eulaTextPlaceholder')}
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {t('panel.eulaTextHint')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 mt-4">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('panel.credentialsTitle')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {t('panel.credentialsSubtitle')}
              </p>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('applicationDetail.appStoreConnectSecretLabel')}
                </label>
                <select
                  value={mobilForm.appStoreSecretId}
                  onChange={e => {
                    setMobilForm(f => ({ ...f, appStoreSecretId: e.target.value }));
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('common.selectPlaceholder')}</option>
                  {appStoreSecrets.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('panel.appStoreIapSecret')}
                </label>
                <select
                  value={mobilForm.appStoreIapSecretId}
                  onChange={e => {
                    setMobilForm(f => ({ ...f, appStoreIapSecretId: e.target.value }));
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('common.selectPlaceholder')}</option>
                  {appStoreIapSecrets.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('panel.appStoreIapSecretHint')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('applicationDetail.playStoreConsoleSecretLabel')}
                </label>
                <select
                  value={mobilForm.playStoreSecretId}
                  onChange={e => {
                    setMobilForm(f => ({ ...f, playStoreSecretId: e.target.value }));
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('common.selectPlaceholder')}</option>
                  {playStoreSecrets.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('panel.githubSecret')}
                </label>
                <select
                  value={mobilForm.githubSecretId}
                  onChange={e => {
                    setMobilForm(f => ({ ...f, githubSecretId: e.target.value }));
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('common.selectPlaceholder')}</option>
                  {githubSecrets.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('panel.githubSecretHint')}
                </p>
              </div>
              {mobilForm.githubSecretId && (
                <div className="pt-1 space-y-2">
                  {app?.pageHosting?.enabled && !editingPageHosting ? (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('panel.pageHostingEnabledLabel')}
                      </p>
                      <ul className="text-sm space-y-1.5">
                        <li>
                          <span className="text-gray-500 dark:text-gray-400 mr-1">
                            {t('panel.pageHostingPreviewLink')}:
                          </span>
                          <a
                            href={`https://${app.pageHosting.owner}.github.io/${app.pageHosting.repo}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 dark:text-primary-400 hover:underline break-all"
                          >
                            {`https://${app.pageHosting.owner}.github.io/${app.pageHosting.repo}/`}
                          </a>
                        </li>
                        <li>
                          <span className="text-gray-500 dark:text-gray-400 mr-1">
                            {t('panel.pageHostingRepoLink')}:
                          </span>
                          <a
                            href={`https://github.com/${app.pageHosting.owner}/${app.pageHosting.repo}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 dark:text-primary-400 hover:underline break-all"
                          >
                            {app.pageHosting.owner}/{app.pageHosting.repo}
                          </a>
                        </li>
                      </ul>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={disablingPages}
                          onClick={() => {
                            setEditingPageHosting(true);
                          }}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-800 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                        >
                          {t('panel.changeGitHubPages')}
                        </button>
                        <button
                          type="button"
                          disabled={disablingPages}
                          onClick={() => setShowDisablePagesModal(true)}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 disabled:opacity-50"
                        >
                          {t('panel.disableGitHubPages')}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('panel.disableGitHubPagesHint')}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('panel.githubPagesOwner')}
                        </label>
                        <select
                          value={githubOwner}
                          onChange={e => setGithubOwner(e.target.value)}
                          disabled={ownersLoading || githubOwners.length === 0}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          {ownersLoading ? (
                            <option value="">{t('common.loading')}</option>
                          ) : githubOwners.length === 0 ? (
                            <option value="">{t('panel.githubPagesOwnerEmpty')}</option>
                          ) : (
                            githubOwners.map(o => (
                              <option key={o.login} value={o.login}>
                                {o.type === 'Organization'
                                  ? t('panel.githubOwnerOrg', { login: o.login })
                                  : t('panel.githubOwnerUser', { login: o.login })}
                              </option>
                            ))
                          )}
                        </select>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {t('panel.githubPagesOwnerHint')}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('panel.githubPagesRepo')}
                        </label>
                        <div className="relative" ref={repoSelectRef}>
                          <button
                            type="button"
                            disabled={reposLoading || ownersLoading}
                            onClick={() => {
                              setRepoSearch('');
                              if (repoDropdownOpen) {
                                setRepoDropdownOpen(false);
                                return;
                              }
                              updateRepoMenuPos();
                              setRepoDropdownOpen(true);
                            }}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-left flex items-center justify-between gap-2 disabled:opacity-50"
                          >
                            <span className="truncate">
                              {reposLoading ? t('common.loading') : selectedGithubRepoLabel}
                            </span>
                            <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
                          </button>
                          {repoDropdownOpen &&
                            !reposLoading &&
                            repoMenuPos &&
                            createPortal(
                              <div
                                ref={repoMenuRef}
                                style={{
                                  position: 'fixed',
                                  left: repoMenuPos.left,
                                  width: repoMenuPos.width,
                                  top: repoMenuPos.openUp ? undefined : repoMenuPos.top,
                                  bottom: repoMenuPos.openUp ? repoMenuPos.bottom : undefined,
                                  zIndex: 80,
                                }}
                                className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl overflow-hidden"
                              >
                                <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                                  <input
                                    type="text"
                                    value={repoSearch}
                                    onChange={e => setRepoSearch(e.target.value)}
                                    placeholder={t('panel.githubPagesRepoSearch')}
                                    autoFocus
                                    className="w-full px-2.5 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                                  />
                                </div>
                                <ul className="max-h-56 overflow-y-auto py-1">
                                  <li>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setGithubRepo(NEW_GITHUB_REPO);
                                        setRepoDropdownOpen(false);
                                        setRepoSearch('');
                                      }}
                                      className={`w-full text-left px-3 py-2 text-sm font-medium ${
                                        githubRepo === NEW_GITHUB_REPO
                                          ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                          : 'text-primary-700 dark:text-primary-300 hover:bg-gray-50 dark:hover:bg-gray-700/80'
                                      }`}
                                    >
                                      {t('panel.githubPagesCreateNewRepo')}
                                    </button>
                                  </li>
                                  {filteredGithubRepos.length === 0 ? (
                                    <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                      {repoSearch.trim()
                                        ? t('panel.githubPagesRepoNoMatch')
                                        : t('panel.githubPagesRepoEmpty')}
                                    </li>
                                  ) : (
                                    filteredGithubRepos.map(repo => (
                                      <li key={repo.name}>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setGithubRepo(repo.name);
                                            setRepoDropdownOpen(false);
                                            setRepoSearch('');
                                          }}
                                          className={`w-full text-left px-3 py-2 text-sm ${
                                            githubRepo === repo.name
                                              ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                              : 'text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/80'
                                          }`}
                                        >
                                          {repo.name}
                                          {repo.private
                                            ? ` (${t('panel.githubRepoPrivate')})`
                                            : ''}
                                        </button>
                                      </li>
                                    ))
                                  )}
                                </ul>
                              </div>,
                              document.body
                            )}
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {t('panel.githubPagesRepoHint')}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={
                            enablingPages ||
                            !isPro ||
                            !githubOwner ||
                            !githubRepo ||
                            ownersLoading ||
                            reposLoading
                          }
                          onClick={async () => {
                            if (!id) return;
                            if (!isPro) {
                              goToUpgrade();
                              return;
                            }
                            setEnablingPages(true);
                            try {
                              if (hasUnsavedChanges) {
                                await persistApplication();
                              }
                              const enabled = await enablePageHosting(id, {
                                owner: githubOwner || undefined,
                                repo:
                                  githubRepo === NEW_GITHUB_REPO
                                    ? undefined
                                    : githubRepo.trim() || undefined,
                              });
                              setApp(enabled);
                              setEditingPageHosting(false);
                            } catch (e) {
                              const msg =
                                e instanceof Error
                                  ? e.message
                                  : t('panel.pageHostingEnableFailed');
                              showErrorPopup(msg);
                            } finally {
                              setEnablingPages(false);
                            }
                          }}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                        >
                          {enablingPages ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                          {editingPageHosting
                            ? t('panel.saveGitHubPages')
                            : t('panel.enableGitHubPages')}
                          {!isPro ? <ProTag /> : null}
                        </button>
                        {editingPageHosting && (
                          <button
                            type="button"
                            disabled={enablingPages}
                            onClick={() => setEditingPageHosting(false)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                          >
                            {t('common.cancel')}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {copyModalOpen && copySourceVersion && (
        <ModalLayout
          open
          onClose={() => setCopyModalOpen(false)}
          closeDisabled={submitting}
          title={t('applicationDetail.copyVersionTitle')}
          icon={<Copy className="h-5 w-5 text-primary-500" />}
          description={t('applicationDetail.copyVersionDescription', { version: copySourceVersion.version })}
          primaryLabel={submitting ? t('applicationDetail.copying') : t('applicationDetail.copyAndOpen')}
          onPrimary={() => void handleCopyVersion({ preventDefault: () => {}, stopPropagation: () => {} } as React.FormEvent)}
          primaryDisabled={submitting || !copyForm.version.trim()}
          primaryLoading={submitting}
          maxWidth="md"
        >
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('applicationDetail.newVersionLabel')}</label>
              <input
                type="text"
                value={copyForm.version}
                onChange={e => setCopyForm(f => ({ ...f, version: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                placeholder={t('applicationDetail.versionPlaceholder')}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('version.whatsNewLabel')} <span className="text-gray-400 font-normal">{t('applicationDetail.primaryLangMax4000')}</span>
              </label>
              <textarea
                rows={4}
                maxLength={4000}
                value={copyForm.releaseNotes}
                onChange={e => setCopyForm(f => ({ ...f, releaseNotes: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                placeholder={t('version.releaseNotesPlaceholder')}
              />
              <p className="mt-1 text-xs text-gray-500">{copyForm.releaseNotes.length}/4000</p>
            </div>
          </div>
        </ModalLayout>
      )}

      <ModalLayout
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        closeDisabled={submitting}
        title={t('applicationDetail.newVersionTitle')}
        icon={<Plus className="h-5 w-5 text-primary-500" />}
        primaryLabel={submitting ? t('applicationDetail.creating') : t('applicationDetail.create')}
        onPrimary={() => void handleCreateVersion({ preventDefault: () => {}, stopPropagation: () => {} } as React.FormEvent)}
        primaryDisabled={submitting || !form.version.trim()}
        primaryLoading={submitting}
        maxWidth="sm"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('applicationDetail.versionColumnLabel')}</label>
          <input
            type="text"
            value={form.version}
            onChange={e => setForm(f => ({ ...f, version: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            placeholder={t('applicationDetail.versionPlaceholder')}
            required
          />
        </div>
      </ModalLayout>

      {showDisablePagesModal && (
        <ModalLayout
          open={showDisablePagesModal}
          onClose={() => {
            if (!disablingPages) setShowDisablePagesModal(false);
          }}
          closeDisabled={disablingPages}
          title={t('panel.disableGitHubPages')}
          icon={<AlertTriangle className="h-5 w-5 text-amber-500" />}
          description={t('panel.confirmDisablePageHosting')}
          primaryLabel={
            disablingPages ? t('common.saving') : t('panel.disableGitHubPagesConfirm')
          }
          onPrimary={() => {
            void (async () => {
              if (!id) return;
              setDisablingPages(true);
              try {
                const updated = await disablePageHosting(id);
                setApp(updated);
                setEditingPageHosting(false);
                setShowDisablePagesModal(false);
              } catch (e) {
                const msg =
                  e instanceof Error ? e.message : t('panel.pageHostingDisableFailed');
                showErrorPopup(msg);
              } finally {
                setDisablingPages(false);
              }
            })();
          }}
          primaryDisabled={disablingPages}
          primaryLoading={disablingPages}
          cancelLabel={t('common.cancel')}
          maxWidth="sm"
        >
          <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
            {app?.pageHosting?.owner && app?.pageHosting?.repo ? (
              <>
                <p>
                  <span className="text-gray-500 dark:text-gray-400 mr-1">
                    {t('panel.pageHostingPreviewLink')}:
                  </span>
                  <a
                    href={`https://${app.pageHosting.owner}.github.io/${app.pageHosting.repo}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-xs break-all text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {`https://${app.pageHosting.owner}.github.io/${app.pageHosting.repo}/`}
                  </a>
                </p>
                <p>
                  <span className="text-gray-500 dark:text-gray-400 mr-1">
                    {t('panel.pageHostingRepoLink')}:
                  </span>
                  <a
                    href={`https://github.com/${app.pageHosting.owner}/${app.pageHosting.repo}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-xs break-all text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {app.pageHosting.owner}/{app.pageHosting.repo}
                  </a>
                </p>
              </>
            ) : null}
            <p>{t('panel.disableGitHubPagesHint')}</p>
          </div>
        </ModalLayout>
      )}

      {showDeployModal && (
        <ModalLayout
          open={showDeployModal}
          onClose={() => setShowDeployModal(false)}
          closeDisabled={deploying}
          title={t('panel.exportFastlaneModalTitle')}
          icon={<Upload className="h-5 w-5 text-primary-500" />}
          description={t('panel.exportFastlaneModalDescription')}
          primaryLabel={
            deploying
              ? t('panel.exportFastlaneRunning')
              : `${t('panel.deploy')} (${deployPlatforms.length})`
          }
          onPrimary={() => {
            void handleConfirmDeploy();
          }}
          primaryDisabled={
            deploying ||
            !deployVersionId ||
            deployPlatforms.length === 0 ||
            (deployPlatforms.includes(PLATFORM_IOS) && !(deployVersion?.supportUrl ?? '').trim())
          }
          primaryLoading={deploying}
          maxWidth="md"
        >
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {t('panel.exportFastlaneSelectVersion')}
              </label>
              <select
                value={deployVersionId}
                onChange={e => {
                  void handleDeployVersionChange(e.target.value);
                }}
                disabled={deploying}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                {versions.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.version}
                  </option>
                ))}
              </select>
            </div>
            <PlatformSelectCards
              options={[
                { value: PLATFORM_IOS, label: t('panel.appStore') },
                { value: PLATFORM_ANDROID, label: t('panel.playStore') },
              ]}
              selected={deployPlatforms}
              onToggle={toggleDeployPlatform}
            />
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={deployIncludeScreens}
                onChange={e => setDeployIncludeScreens(e.target.checked)}
                disabled={deploying}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              {t('panel.exportFastlaneIncludeScreens')}
            </label>
            {deployIncludeScreens && deployPlatforms.includes(PLATFORM_IOS) && (
              <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deployAppPreviewPrimaryOnly}
                  onChange={e => setDeployAppPreviewPrimaryOnly(e.target.checked)}
                  disabled={deploying}
                  className="mt-0.5 rounded border-gray-300 dark:border-gray-600"
                />
                <span>
                  {t('appScreen.uploadAppPreviewPrimaryOnly')}
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t('appScreen.uploadAppPreviewPrimaryOnlyHint')}
                  </span>
                </span>
              </label>
            )}
            {deployPlatforms.includes(PLATFORM_IOS) && !(deployVersion?.supportUrl ?? '').trim() && (
              <p className="text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
                {t('panel.metadataUploadSupportUrlRequired')}
              </p>
            )}
            {deployPlatforms.includes(PLATFORM_IOS) && iosDeployIssues.length > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200">
                <p className="font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {t('panel.iosUploadReadinessTitle')}
                </p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  {iosDeployIssues.includes('reviewContactUser') && (
                    <li>{t('panel.iosUploadIssueReviewContact')}</li>
                  )}
                  {iosDeployIssues.includes('iosBundleId') && (
                    <li>{t('panel.iosUploadIssueBundleId')}</li>
                  )}
                  {iosDeployIssues.includes('iosPricing') && (
                    <li>{t('panel.iosUploadIssuePricing')}</li>
                  )}
                  {iosDeployIssues.includes('iosAvailability') && (
                    <li>{t('panel.iosUploadIssueAvailability')}</li>
                  )}
                  {iosDeployIssues.includes('iosAvailabilityTerritories') && (
                    <li>{t('panel.iosUploadIssueAvailabilityTerritories')}</li>
                  )}
                  {iosDeployIssues.includes('customEulaText') && (
                    <li>{t('panel.iosUploadIssueCustomEulaText')}</li>
                  )}
                  {iosDeployIssues.includes('primaryCategory') && (
                    <li>{t('panel.iosUploadIssuePrimaryCategory')}</li>
                  )}
                </ul>
              </div>
            )}
            {deployPlatforms.includes(PLATFORM_ANDROID) && androidDeployIssues.length > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200">
                <p className="font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {t('panel.androidUploadReadinessTitle')}
                </p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  {androidDeployIssues.includes('androidBundleId') && (
                    <li>{t('panel.androidUploadIssueBundleId')}</li>
                  )}
                  {androidDeployIssues.includes('androidAvailability') && (
                    <li>{t('panel.androidUploadIssueAvailability')}</li>
                  )}
                  {androidDeployIssues.includes('androidAvailabilityCountries') && (
                    <li>{t('panel.androidUploadIssueAvailabilityCountries')}</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </ModalLayout>
      )}

      <ModalLayout
        open={!!fixWarning}
        onClose={() => {
          if (!fixWarningSaving) setFixWarning(null);
        }}
        title={
          fixWarning?.code === 'ASC_NAME_IN_USE'
            ? t('panel.appWarningFixNameTitle', { locale: fixWarning.locale || '—' })
            : t('panel.appWarningsFix')
        }
        icon={<AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
        description={
          fixWarning?.code === 'ASC_NAME_IN_USE'
            ? t('panel.appWarningFixNameDescription')
            : fixWarning?.message
        }
        primaryLabel={
          fixWarningSaving ? t('panel.appWarningFixSaving') : t('panel.appWarningFixSave')
        }
        onPrimary={() => {
          void handleSaveFixWarning();
        }}
        primaryDisabled={
          fixWarningSaving ||
          (fixWarning?.code === 'ASC_NAME_IN_USE' && !fixNameValue.trim())
        }
        primaryLoading={fixWarningSaving}
        closeDisabled={fixWarningSaving}
        cancelLabel={t('common.cancel')}
      >
        {fixWarning?.code === 'ASC_NAME_IN_USE' && (
          <div>
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('panel.appWarningFixNameLabel')}
                {fixWarning.locale ? (
                  <span className="ml-2 font-mono normal-case text-gray-400">
                    {getLocaleFlag(fixWarning.locale)} {fixWarning.locale}
                  </span>
                ) : null}
              </span>
              <input
                type="text"
                value={fixNameValue}
                onChange={e => setFixNameValue(e.target.value)}
                maxLength={30}
                disabled={fixWarningSaving}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                autoFocus
              />
            </label>
          </div>
        )}
      </ModalLayout>

      {deleteModalOpen && app && (
        <ConfirmDialog
          open={deleteModalOpen}
          onClose={() => {
            if (!deleting) setDeleteModalOpen(false);
          }}
          title={t('panel.deleteAppTitle')}
          message={t('panel.deleteAppConfirm', { name: app.name })}
          confirmLabel={deleting ? t('panel.deleting') : t('common.delete')}
          cancelLabel={t('common.cancel')}
          onConfirm={() => void handleDeleteConfirm()}
          confirmLoading={deleting}
          closeDisabled={deleting}
          variant="danger"
        />
      )}

      <ConfirmDialog
        open={Boolean(deleteVersionTarget)}
        onClose={() => {
          if (!deletingVersion) setDeleteVersionTarget(null);
        }}
        title={t('panel.deleteVersionTitle')}
        message={t('panel.deleteVersionConfirm', {
          version: deleteVersionTarget?.version ?? '',
        })}
        confirmLabel={deletingVersion ? t('common.deleting') : t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void handleDeleteVersionConfirm()}
        confirmLoading={deletingVersion}
        closeDisabled={deletingVersion}
        variant="danger"
      />
      <OnboardingTour
        tourId="app-detail"
        steps={[
          {
            target: '[data-tour="appdetail-icon-colors"]',
            title: t('onboarding.appDetail.step1Title'),
            body: t('onboarding.appDetail.step1Body'),
          },
          {
            target: '[data-tour="appdetail-versions"]',
            title: t('onboarding.appDetail.step2Title'),
            body: t('onboarding.appDetail.step2Body'),
          },
          {
            target: '[data-tour="appdetail-deploy"]',
            title: t('onboarding.appDetail.step3Title'),
            body: t('onboarding.appDetail.step3Body'),
          },
          {
            target: '[data-tour="appdetail-general-info"]',
            title: t('onboarding.appDetail.step4Title'),
            body: t('onboarding.appDetail.step4Body'),
          },
        ]}
        autoStart
      />
      </>
      )}
    </AppPageLayout>
  );
};

export default ApplicationDetail;
